import { useMutation, useQuery } from '@apollo/client';
import React, { useCallback, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';
import AccountContentContainer from './AccountContentContainer';
import canAuthenticateWithPassword from './canAuthenticateWithPassword';
import * as styles from './InvitationView.module.scss';
import LoginHandler from './LoginHandler';
import usePostLoginHandler from './usePostLoginHandler';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Form, { Field, Submit } from '../components/Form';
import LoadingIndicator from '../components/LoadingIndicator';
import { ACTION_INVITE_ACCEPT, CATEGORY_USER } from '../constants/analytics';
import { gql } from '../graphql/generated/gql';
import { UIActions } from '../ui/uiSlice';
import { track } from '../utils/analytics';
import IntegrationLoginButtons from './login/IntegrationLoginButtons';
import Link from '../utils/router/Link';
import useNavigate from '../utils/router/useNavigate';

const INVITATION_QUERY = gql(/** @complexity 1 */ `
  query invitation($id: ID!) {
    invitation(id: $id) {
      id
      email
      name
      acceptedAt
      type
    }
  }
`);

const ACCEPT_INVITATION_MUTATION = gql(/** @complexity 2 */ `
  mutation acceptInvitation($id: ID!, $password: String!, $name: String!, $secret: String!) {
    session: acceptInvitation(id: $id, password: $password, name: $name, secret: $secret) {
      authToken
    }
  }
`);

interface InvitationValues {
  email: string;
  name: string;
  password: string;
}

const InvitationView = () => {
  const navigate = useNavigate();
  const params = useParams() as { id: string };
  const [searchParams] = useSearchParams();
  // fall back to id for backwards compatibility with old invitations
  const secret = searchParams.get('secret') ?? params.id;
  const dispatch = useAppDispatch();

  const organization = window.__slab.PUBLIC_ORG;

  const { data, loading } = useQuery(INVITATION_QUERY, {
    variables: { id: params.id },
  });

  const [acceptInvitation] = useMutation(ACCEPT_INVITATION_MUTATION);

  const invitation = data?.invitation;

  const handleAcceptedError = useCallback(() => {
    navigate('/login');
    dispatch(
      UIActions.pushToast(
        {
          level: 'error',
          message: 'Invitation already accepted. Please login normally.',
        },
        3000,
      ),
    );
  }, [navigate, dispatch]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!invitation) {
      navigate('/login');
      dispatch(
        UIActions.pushToast(
          {
            level: 'error',
            message:
              'Oops, we could not find your invitation. Please check the URL or ask your teammate to re-send an invitation.',
          },
          10000,
        ),
      );
    } else if (invitation.acceptedAt) {
      handleAcceptedError();
    } else if (
      !canAuthenticateWithPassword(
        organization,
        organization.ssoPlatforms,
        invitation.type,
      )
    ) {
      navigate('/login');
      dispatch(
        UIActions.pushToast(
          {
            level: 'warning',
            message: `Please use Single Sign-On to join ${organization.name}.`,
          },
          3000,
        ),
      );
    }
  }, [
    loading,
    invitation,
    organization,
    navigate,
    dispatch,
    handleAcceptedError,
  ]);

  const postLoginHandler = usePostLoginHandler();

  const onSubmit = async (values: InvitationValues) => {
    const { data: acceptedInvitationData } = await acceptInvitation({
      variables: {
        id: params.id,
        secret,
        password: values.password,
        name: values.name,
      },
    });
    track(ACTION_INVITE_ACCEPT, { category: CATEGORY_USER });
    if (acceptedInvitationData?.session) {
      postLoginHandler(acceptedInvitationData.session.authToken);
    }
  };

  const handleSeatLimitError = () => {
    navigate('/login');
    dispatch(
      UIActions.pushToast(
        {
          level: 'error',
          message:
            'Your team has reached its seat limit. Please upgrade or contact support.',
        },
        3000,
      ),
    );
  };

  const { ssoPlatforms } = organization;

  return (
    <AccountContentContainer
      title={`Welcome to ${organization.name}`}
      subtitle="Let's set up your account!"
    >
      <LoginHandler>
        {loading || !invitation ? (
          <div className={styles.loadingWrapper}>
            <LoadingIndicator />
          </div>
        ) : (
          <>
            <Form<InvitationValues>
              apiErrorHandlers={{
                TOKEN_REUSE: handleAcceptedError,
                SEAT_LIMIT_MAXED: handleSeatLimitError,
              }}
              onSubmit={onSubmit}
              initialValues={{
                email: invitation.email ?? '',
                name: invitation.name ?? '',
                password: '',
              }}
            >
              <Field
                required
                label="Your full name"
                type="text"
                name="name"
                autoComplete="name"
              />
              <Field
                disabled
                type="email"
                label="Email"
                name="email"
                autoComplete="username email"
              />
              <Field
                required
                label="Password"
                type="password"
                name="password"
                autoComplete="new-password"
              />
              <Submit block>Sign up</Submit>
              <div className={styles.loginLink}>
                Already have an account? <Link to="/login">Login</Link>
              </div>
            </Form>
            {ssoPlatforms.length > 0 && (
              <>
                <AccountContentContainer.Divider />
                <IntegrationLoginButtons
                  email={organization.email}
                  ssoPlatforms={ssoPlatforms}
                  register
                />
              </>
            )}
          </>
        )}
      </LoginHandler>
    </AccountContentContainer>
  );
};

export default InvitationView;
